import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "./cn";
import FloatingPanel from "./FloatingPanel";

/**
 * Bun tooltip: ink chip, 11/450, radius 7, small arrow. Hover/focus only,
 * for labels that don't fit (icon buttons). Note: renders inline (not a
 * portal), so avoid inside overflow-hidden containers; keep labels short.
 */
export default function Tooltip({
  label,
  side = "top",
  portal = false,
  open,
  onOpenChange,
  anchorRef,
  id,
  multiline = false,
  hoverable = false,
  variant = hoverable ? "surface" : "tooltip",
  width,
  maxHeight,
  align = "end",
  anchorClassName,
  className,
  children,
}) {
  const managedAnchorRef = useRef(null);
  const closeTimerRef = useRef(null);
  const generatedId = useId();
  const [managedOpen, setManagedOpen] = useState(false);
  const isOpen = open ?? managedOpen;
  const tooltipId = id || generatedId;
  const setOpen = useCallback((nextOpen) => {
    setManagedOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [onOpenChange]);
  const clearCloseTimer = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);
  const show = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer, setOpen]);
  const hide = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer, setOpen]);
  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    if (!hoverable) {
      setOpen(false);
      return;
    }
    closeTimerRef.current = setTimeout(() => {
      if (managedAnchorRef.current?.contains(document.activeElement)) return;
      setOpen(false);
    }, 120);
  }, [clearCloseTimer, hoverable, setOpen]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);
  useEffect(() => {
    if (!isOpen || (open !== undefined && !onOpenChange)) return undefined;
    const onEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      hide();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [hide, isOpen, open, onOpenChange]);

  if (portal) {
    const panel = (
      <FloatingPanel
        open={isOpen}
        anchorRef={anchorRef || managedAnchorRef}
        align={align}
        variant={variant}
        width={width ?? (multiline || hoverable ? 320 : "max-content")}
        maxHeight={maxHeight ?? (multiline || hoverable ? 256 : 48)}
        id={tooltipId}
        role="tooltip"
        onMouseEnter={hoverable ? show : undefined}
        onMouseLeave={hoverable ? scheduleClose : undefined}
        className={cn(
          !hoverable && "pointer-events-none",
          hoverable
            ? "p-3 text-bun-meta font-book"
            : multiline
            ? "whitespace-pre-wrap break-words px-3 py-2.5 text-bun-meta font-book normal-case leading-relaxed tracking-normal"
            : "whitespace-nowrap px-[9px] py-[5px] text-bun-label font-book leading-tight tracking-normal",
          className
        )}
      >
        {label}
      </FloatingPanel>
    );

    if (children && !anchorRef) {
      return (
        <span
          ref={managedAnchorRef}
          data-bun-tooltip-anchor
          className={cn("inline-flex", anchorClassName)}
          onBlur={scheduleClose}
          onFocus={show}
          onMouseEnter={show}
          onMouseLeave={scheduleClose}
        >
          {React.isValidElement(children) ? React.cloneElement(children, {
            "aria-describedby": isOpen ? tooltipId : undefined,
            ...(hoverable ? {
              "aria-controls": tooltipId,
              "aria-expanded": isOpen,
              onClick: (event) => {
                children.props.onClick?.(event);
                if (!event.defaultPrevented) show();
              },
            } : {}),
          }) : children}
          {panel}
        </span>
      );
    }

    return panel;
  }

  const pos =
    side === "bottom"
      ? "top-full mt-[6px]"
      : "bottom-full mb-[6px]";
  const arrow =
    side === "bottom"
      ? "bottom-full border-b-bun-ink border-x-transparent border-t-transparent"
      : "top-full border-t-bun-ink border-x-transparent border-b-transparent";
  return (
    <span data-bun-tooltip-anchor className={cn("relative inline-flex group/tt", className)}>
      {children}
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute left-1/2 -translate-x-1/2 z-40 whitespace-nowrap",
          "opacity-0 group-hover/tt:opacity-100 group-focus-within/tt:opacity-100 transition-opacity duration-100",
          "font-instrument text-bun-label normal-case tracking-normal font-book",
          "bg-bun-ink text-bun-on-ink rounded-bun-sm px-[9px] py-[5px]",
          pos
        )}
      >
        {label}
        <span
          className={cn(
            "absolute left-1/2 -translate-x-1/2 border-[5px]",
            arrow
          )}
        />
      </span>
    </span>
  );
}

/** Opt-in action help for a page, including its React portals. Existing tooltips win. */
function TooltipScope({ children }) {
  const [target, setTarget] = useState(null);
  const anchorRef = useRef(null);
  const id = useId();
  anchorRef.current = target;
  const label = target && (
    target.getAttribute('data-tooltip') || target.getAttribute('aria-label') ||
    target.getAttribute('title') ||
    Array.from(target.labels || []).map((item) => item.textContent).join(' ').trim() ||
    target.textContent.trim()
  );

  useEffect(() => {
    if (!target || !label) return undefined;
    const previous = target.getAttribute('aria-describedby');
    target.setAttribute('aria-describedby', [previous, id].filter(Boolean).join(' '));
    return () => {
      const remaining = (target.getAttribute('aria-describedby') || '').split(' ').filter((value) => value && value !== id).join(' ');
      if (remaining) target.setAttribute('aria-describedby', remaining);
      else target.removeAttribute('aria-describedby');
    };
  }, [target, label, id]);

  const show = (event) => {
    const button = event.target.closest?.('button, [role="button"], [role="switch"]');
    setTarget(button?.closest('[data-bun-tooltip-anchor], [data-tooltip="false"]') ? null : button);
  };
  const hide = (event) => {
    if (!event.relatedTarget?.nodeType || !target?.contains(event.relatedTarget)) setTarget(null);
  };

  return (
    <div onMouseOverCapture={show} onMouseOutCapture={hide} onFocusCapture={show} onBlurCapture={hide} onClickCapture={() => setTarget(null)}>
      {children}
      <Tooltip portal anchorRef={anchorRef} id={id} label={label} open={!!target && !!label}
        onOpenChange={(open) => { if (!open) setTarget(null); }} />
    </div>
  );
}

Tooltip.Scope = TooltipScope;
